import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { errors, paxRoute, sessionRequired, staffAuth } from "../../common";
import {
  CreateSupportAdjustmentResponse,
  ListSupportAdjustmentsQuery,
  ListSupportAdjustmentsResponse,
  MAX_SUPPORT_ISSUANCE_CREDITS_PER_DAY,
  SupportAdjustmentRequest,
} from "./schemas";

export const createSupportAdjustment = oc
  .meta(
    paxRoute({ auth: staffAuth("credits.manage"), minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/accounting/v1alpha1/support/adjustments",
      operationId: "accounting.support.adjustments.create",
      summary: "Record a support adjustment.",
      description: `Adds credits to or takes them out of the balance a staff member names, and records who made the change and why. All staff together may grant up to ${MAX_SUPPORT_ISSUANCE_CREDITS_PER_DAY} Pax credits per UTC day.`,
      inputStructure: "detailed",
      successDescription: "The adjustment that was recorded.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...sessionRequired,
    ...errors.forbidden,
    NOT_FOUND: { message: "No player has that id." },
    CONFLICT: {
      message: `That player is closed, the adjustment id was already recorded for a different change, a removal would take a restricted balance below zero, or today's support grants would pass ${MAX_SUPPORT_ISSUANCE_CREDITS_PER_DAY} Pax credits.`,
    },
  })
  .input(z.object({ body: SupportAdjustmentRequest }))
  .output(CreateSupportAdjustmentResponse);

export const listSupportAdjustments = oc
  .meta(
    paxRoute({ auth: staffAuth("credits.view"), minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/accounting/v1alpha1/support/adjustments",
      operationId: "accounting.support.adjustments.list",
      summary: "List support adjustments.",
      description:
        "Returns support grants and removals, newest first, with who made each one and why.",
      inputStructure: "detailed",
      successDescription: "One page of support adjustments.",
    }),
  )
  .errors({ ...errors.badRequest, ...sessionRequired, ...errors.forbidden })
  .input(z.object({ query: ListSupportAdjustmentsQuery }))
  .output(ListSupportAdjustmentsResponse);
